// VOXEL shape-mode drag-paint / drag-erase. Each drag frame uses the
// current laser pick so the cursor visibly follows wherever the user
// is aiming.
//
// DRAW relies on pickRay's voxelRaycastSkip to keep the laser blind
// to voxels we placed earlier in this gesture (`placedCells`). That
// means as the user drags, the pick always reports the original
// surface the gesture started on — placing a voxel on top of a box
// then moving the cursor down the box's front face transitions
// cleanly because the laser "sees through" the row of voxels just
// stamped on top. Re-placement at an already-filled cell is blocked
// by the grid.has check in applyStep.
//
// ERASE keeps the in-plane `affected` key so each column on the
// gesture's plane axis can only be erased once (prevents drilling
// straight through a column when the laser doesn't move).

import { controllerStates } from "../../input/controllers";
import {
  type PaletteIndex,
  type VoxelGrid,
} from "../../model/voxel-grid";
import { mirrorCells } from "../mirror";
import { type CellBounds, type PickTarget, cellInBounds } from "../pick";
import { interaction, setInteraction } from "../state";
import type { Interaction } from "../types";

type VoxelPaintState = Extract<
  Interaction,
  { kind: "voxel_paint" } | { kind: "voxel_erase" } | { kind: "voxel_recolor" }
>;

// FaceNormal index → unit step delta.
const FACE_DELTA: ReadonlyArray<readonly [number, number, number]> = [
  [-1, 0, 0],
  [1, 0, 0],
  [0, -1, 0],
  [0, 1, 0],
  [0, 0, -1],
  [0, 0, 1],
];

function paintTarget(
  pick: PickTarget,
): { ix: number; iy: number; iz: number } | null {
  if (!pick) return null;
  if (pick.kind === "voxel") {
    const [dx, dy, dz] = FACE_DELTA[pick.face];
    return { ix: pick.ix + dx, iy: pick.iy + dy, iz: pick.iz + dz };
  }
  return { ix: pick.ix, iy: pick.iy, iz: pick.iz };
}

function eraseTarget(
  pick: PickTarget,
): { ix: number; iy: number; iz: number } | null {
  if (!pick || pick.kind !== "voxel") return null;
  return { ix: pick.ix, iy: pick.iy, iz: pick.iz };
}

function planeAxisFromPick(pick: PickTarget): 0 | 1 | 2 | null {
  if (!pick) return null;
  if (pick.kind === "voxel") {
    const [nx, ny] = FACE_DELTA[pick.face];
    return nx !== 0 ? 0 : ny !== 0 ? 1 : 2;
  }
  return pick.normal[0] !== 0 ? 0 : pick.normal[1] !== 0 ? 1 : 2;
}

function paintNormalSignFromPick(pick: PickTarget): -1 | 1 | null {
  if (!pick) return null;
  if (pick.kind === "voxel") {
    const [nx, ny, nz] = FACE_DELTA[pick.face];
    const c = nx !== 0 ? nx : ny !== 0 ? ny : nz;
    return c > 0 ? 1 : -1;
  }
  const planeAxis =
    pick.normal[0] !== 0 ? 0 : pick.normal[1] !== 0 ? 1 : 2;
  const c =
    planeAxis === 0
      ? pick.normal[0]
      : planeAxis === 1
        ? pick.normal[1]
        : pick.normal[2];
  return c > 0 ? -1 : 1;
}

export const cellKey3D = (c: {
  ix: number;
  iy: number;
  iz: number;
}): string => `${c.ix},${c.iy},${c.iz}`;

function inPlaneKey(
  target: { ix: number; iy: number; iz: number },
  planeAxis: 0 | 1 | 2,
): string {
  if (planeAxis === 0) return `${target.iy},${target.iz}`;
  if (planeAxis === 1) return `${target.ix},${target.iz}`;
  return `${target.ix},${target.iy}`;
}

function applyStep(
  state: VoxelPaintState,
  target: { ix: number; iy: number; iz: number },
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  // Stamp the target and, while symmetry is on, its mirror cells — each gated by
  // the same bounds / placed / in-plane checks so a mirrored column behaves like
  // the one the cursor is on.
  for (const c of mirrorCells(target.ix, target.iy, target.iz)) {
    if (!cellInBounds(c.ix, c.iy, c.iz, bounds)) continue;
    if (state.kind === "voxel_paint") {
      // grid.has already blocks re-placement at a cell we filled this
      // gesture; combined with pickRay's voxelRaycastSkip on placedCells,
      // that's all we need to prevent the spire/side-creep failure modes
      // for DRAW.
      if (grid.has(c.ix, c.iy, c.iz)) continue;
      grid.set(c.ix, c.iy, c.iz, paletteIndex);
      state.placedCells.add(cellKey3D(c));
    } else if (state.kind === "voxel_recolor") {
      // PAINT: recolor only EXISTING voxels. No geometry changes, so the laser
      // naturally rides the surface (no skip/include mask needed); placedCells
      // just dedupes so each swept voxel is recolored once per gesture.
      if (!grid.has(c.ix, c.iy, c.iz)) continue;
      const k = cellKey3D(c);
      if (state.placedCells.has(k)) continue;
      grid.set(c.ix, c.iy, c.iz, paletteIndex);
      state.placedCells.add(k);
    } else {
      const key = inPlaneKey(c, state.planeAxis);
      if (state.affected.has(key)) continue;
      state.affected.add(key);
      if (!grid.has(c.ix, c.iy, c.iz)) continue;
      grid.delete(c.ix, c.iy, c.iz);
      // Keep the erased cell COLLIDABLE for this gesture (fed back as the
      // raycast `include` mask). Without this the cursor falls through the hole
      // to whatever's behind, so a single click viewed at an angle drills a
      // tunnel — the ray exits the erased cell sideways/backward into the next
      // voxel and erases it too. Anchoring the pick to the original surface
      // depth means a steady cursor erases exactly one voxel, and a drag erases
      // only the surface it sweeps across.
      state.placedCells.add(cellKey3D(c));
    }
  }
}

export function startVoxelPaint(
  initialPick: PickTarget,
  controllerIndex: number,
  action: "draw" | "erase" | "recolor",
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  // RECOLOR targets the picked voxel itself (like ERASE) — it operates on
  // existing voxels, not the empty cell adjacent to the face.
  const target =
    action === "draw" ? paintTarget(initialPick) : eraseTarget(initialPick);
  if (!target) return;
  const planeAxis = planeAxisFromPick(initialPick);
  const paintNormalSign = paintNormalSignFromPick(initialPick);
  if (planeAxis === null || paintNormalSign === null) return;
  const kind =
    action === "draw"
      ? "voxel_paint"
      : action === "erase"
        ? "voxel_erase"
        : "voxel_recolor";
  const state: VoxelPaintState = {
    kind,
    controllerIndex,
    planeAxis,
    paintNormalSign,
    affected: new Set(),
    placedCells: new Set(),
  };
  setInteraction(state);
  applyStep(state, target, grid, bounds, paletteIndex);
}

export function handleVoxelPaintDrag(
  lastPicks: ReadonlyArray<PickTarget | null>,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  if (
    interaction.kind !== "voxel_paint" &&
    interaction.kind !== "voxel_erase" &&
    interaction.kind !== "voxel_recolor"
  ) {
    return;
  }
  const state = interaction;
  if (!controllerStates[state.controllerIndex].selecting) return;
  const pick = lastPicks[state.controllerIndex];
  // Each frame the target follows the pick directly. For DRAW that
  // means whichever face of the original surface the laser is on
  // right now — top, side, front, etc. — gets a voxel placed
  // adjacent. The skip-on-placedCells in pickRay (wired through
  // main.ts) keeps the laser pointing at the original surface
  // instead of climbing onto stamped voxels.
  const target =
    state.kind === "voxel_paint" ? paintTarget(pick) : eraseTarget(pick);
  if (!target) return;
  applyStep(state, target, grid, bounds, paletteIndex);
}
